import { CRMChannel } from "@jugl-web/rest-api";
import { DataLoadingWrapper, MultiSectionLayout } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useSessionStorage } from "@jugl-web/utils/hooks/useStorage";
import { ChatbotPreviewBackground } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/screens/WebChatBotAccountScreen/components/ChatbotPreviewBackground";
import { useManageAccountsDrawer } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { useWebChatBotAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { useWhatsappAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WhatsappAccountProvider/useWhatsappAccount";
import { useInstagramAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/InstagramAccountProvider/useInstagramAccount";
import { useEmailAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/EmailAccountProvider/useEmailAccount";
import { sortConnectedChannels } from "@web-src/modules/common/utils/sortConnectedChannels";
import React, { useMemo } from "react";
import { ChannelSection } from "./components/ChannelSection";
import { DisconnectedChannelPreview } from "./components/DisconnectedChannelPreview";
import { GenericChannelPreview } from "./components/GenericChannelPreview";
import { InstagramTile } from "./components/InstagramTile";
import { LiveChatPreview } from "./components/LiveChatPreview";
import { LiveChatTile } from "./components/LiveChatTile";
import { VoiceTile } from "./components/VoiceTile";
import { TwilioTile } from "./components/TwilioTile";
import { EmailTile } from "./components/EmailTile";
import { EmailPreview } from "./components/EmailPreview";
import { VoicePreview } from "./components/VoicePreview";
import { WebChatPagePreview } from "./components/WebChatPagePreview";
import { WebChatTile } from "./components/WebChatTile";
import { WhatsappTile } from "./components/WhatsappTile";

type SelectedChannel = CRMChannel | "live-chat";

const CHANNEL_TILES: {
  id: SelectedChannel;
  Tile: React.FC<{ isSelected: boolean; onSelect: () => void }>;
}[] = [
  { id: CRMChannel.chatbot, Tile: WebChatTile },
  { id: "live-chat", Tile: LiveChatTile },
  { id: CRMChannel.voice, Tile: VoiceTile },
  { id: CRMChannel.email, Tile: EmailTile },
  { id: CRMChannel.twilio, Tile: TwilioTile },
  { id: CRMChannel.instagram, Tile: InstagramTile },
  { id: CRMChannel.whatsapp, Tile: WhatsappTile },
];

export const PreviewAndInstallTabContent: React.FC<{ entityId: string }> = ({
  entityId,
}) => {
  const { t } = useTranslations();
  const { crmConfig, isCRMConfigLoading, isCRMConfigError, refetchCRMConfig } =
    useManageAccountsDrawer();
  const { isConnected: isWebChatConnected } = useWebChatBotAccount();
  const { isConnected: isWhatsappConnected } = useWhatsappAccount();
  const { isConnected: isInstagramConnected } = useInstagramAccount();
  const { isConnected: isEmailConnected } = useEmailAccount();

  const [selectedChannel, setSelectedChannel] =
    useSessionStorage<SelectedChannel>(
      `cx-selected-channel-${entityId}`,
      CRMChannel.chatbot
    );

  const isChannelConnected = useMemo(
    (): Record<SelectedChannel, boolean> => ({
      [CRMChannel.chatbot]: isWebChatConnected,
      [CRMChannel.whatsapp]: isWhatsappConnected,
      [CRMChannel.instagram]: isInstagramConnected,
      [CRMChannel.twilio]: !!crmConfig?.twilio?.id,
      [CRMChannel.voice]: !!crmConfig?.voice?.id,
      [CRMChannel.email]: isEmailConnected,
      "live-chat": crmConfig?.livechat?.enabled === true,
    }),
    [
      crmConfig?.livechat?.enabled,
      crmConfig?.twilio?.id,
      crmConfig?.voice?.id,
      isEmailConnected,
      isInstagramConnected,
      isWebChatConnected,
      isWhatsappConnected,
    ]
  );

  const isSelectedChannelConnected = isChannelConnected[selectedChannel];

  const { connectedTiles, availableTiles } = useMemo(() => {
    const groups = CHANNEL_TILES.reduce(
      (acc, channel) => {
        acc[
          isChannelConnected[channel.id] ? "connectedTiles" : "availableTiles"
        ].push(channel);
        return acc;
      },
      {
        connectedTiles: [] as typeof CHANNEL_TILES,
        availableTiles: [] as typeof CHANNEL_TILES,
      }
    );

    return {
      connectedTiles: sortConnectedChannels(groups.connectedTiles, crmConfig),
      availableTiles: groups.availableTiles,
    };
  }, [crmConfig, isChannelConnected]);

  const renderChannelPreview = () => {
    if (selectedChannel !== CRMChannel.chatbot && !isSelectedChannelConnected) {
      return <DisconnectedChannelPreview channel={selectedChannel} />;
    }

    switch (selectedChannel) {
      case CRMChannel.chatbot:
        return <WebChatPagePreview entityId={entityId} />;
      case CRMChannel.whatsapp:
      case CRMChannel.instagram:
      case CRMChannel.twilio:
        return (
          <ChatbotPreviewBackground>
            <GenericChannelPreview
              channel={selectedChannel}
              entityId={entityId}
              isPreview
            />
          </ChatbotPreviewBackground>
        );
      case CRMChannel.voice:
        return <VoicePreview />;
      case CRMChannel.email:
        return <EmailPreview entityId={entityId} />;
      case "live-chat":
        return <LiveChatPreview />;
      default:
        return null;
    }
  };

  return (
    <DataLoadingWrapper
      isLoading={isCRMConfigLoading}
      isError={isCRMConfigError}
      onRetry={refetchCRMConfig}
    >
      <MultiSectionLayout
        leftSide={
          <div className="jugl__custom-scrollbar flex h-full w-116 flex-col gap-8 overflow-y-auto border-0 border-r border-solid border-dark-200 p-8">
            {connectedTiles.length > 0 && (
              <ChannelSection
                title={t({
                  id: "common.connected",
                  defaultMessage: "Connected",
                })}
              >
                {connectedTiles.map(({ id, Tile }) => (
                  <Tile
                    key={id}
                    isSelected={selectedChannel === id}
                    onSelect={() => setSelectedChannel(id)}
                  />
                ))}
              </ChannelSection>
            )}
            {availableTiles.length > 0 && (
              <ChannelSection
                title={t({
                  id: "cx-ai-agents-page.available-channels",
                  defaultMessage: "Available Channels",
                })}
              >
                {availableTiles.map(({ id, Tile }) => (
                  <Tile
                    key={id}
                    isSelected={selectedChannel === id}
                    onSelect={() => setSelectedChannel(id)}
                  />
                ))}
              </ChannelSection>
            )}
          </div>
        }
        className="animate-fade-in"
        classNames={{
          content: "flex items-center justify-center",
        }}
      >
        {renderChannelPreview()}
      </MultiSectionLayout>
    </DataLoadingWrapper>
  );
};
